import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import LocalizedLink from '@/components/shared/LocalizedLink';

export default function HelpPage() {
  const { t } = useTranslation('help');
  const { lang } = useLanguage();

  // Locale-aware image path helper
  const helpImg = (name: string) => `/images/help/${lang === 'en' ? 'en/' : ''}${name}`;

  return (
    <><PageHeader title={t('pageTitle')} />
      <div className="max-w-[1200px]">
        {/* Верхняя секция: Видео + Карточки */}
        <div className="flex flex-col xl:flex-row gap-[24px] mb-[24px] items-start">
          {/* Видео блок (только для RU) */}
          {lang === 'ru' && (
            <div className="w-full xl:w-[580px] flex-shrink-0">
              <div className="bg-[var(--grey-background,#f8f8f8)] rounded-[16px] overflow-hidden">
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    src="https://www.tella.tv/video/cmik9cn0400ab04lbgezz6mlh/embed?b=0&title=0&a=1&loop=0&t=0&muted=0&wt=1"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          )}

          {/* Карточки справа */}
          <div className={`flex flex-col gap-[16px] w-full ${lang === 'ru' ? 'xl:w-[524px]' : 'max-w-[580px]'}`}>
            {/* Карточка отзыва */}
            <div className="bg-white rounded-[16px] p-[24px] flex flex-col gap-[16px]">
              <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
                {t('feedback.title')}
              </h3>
              <Button
                asChild
                className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
              >
                <a href={lang === 'en' ? "https://create-products.com/en/testimonial" : "https://create-products.com/testimonial"} target="_blank" rel="noopener noreferrer">
                  {t('feedback.button')}
                </a>
              </Button>
            </div>

            {/* Карточка услуги */}
            <div className="bg-white rounded-[16px] p-[24px] flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[8px]">
                <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('service.title')}
                </h3>
                <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text-,#565858)]">
                  {t('service.description')}
                </p>
              </div>
              <Button
                asChild
                className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
              >
                <a href={lang === 'en' ? "https://create-products.com/en/service" : "https://create-products.com/service"} target="_blank" rel="noopener noreferrer">
                  {t('service.button')}
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Контактная информация */}
        <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text-,#565858)] mb-[40px]">
          {t('contact.text')}{' '}
          <a href="mailto:mail@create-products.com" className="underline">
            mail@create-products.com
          </a>
        </p>

        {/* Пошаговая инструкция */}
        <div className="flex flex-col gap-[32px] max-w-[580px]">
          {/* Шаг 1: Проект */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step1.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step1.intro1')}</p>
              <p>{t('step1.intro2')}</p>
              <p>{t('step1.description')}</p>
            </div>
            <img
              src={helpImg('step1-project.png')}
              alt="Создание проекта"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step1.example')}
            </p>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/dashboard" state={{ openCreateProject: true }}>{t('step1.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Шаг 2: Анализ рынка */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step2.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step2.intro')}</p>
              <p>{t('step2.description')}</p>
            </div>
            <img
              src={helpImg('step2-market-form.png')}
              alt="Форма анализа рынка"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <img
              src={helpImg('step2-market-result.png')}
              alt="Результат анализа рынка"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              <p className="mb-[8px]">{t('step2.resultIntro')}</p>
              <ul className="list-disc pl-[24px] space-y-[8px]">
                <li>{t('step2.result1')}</li>
                <li>{t('step2.result2')}</li>
                <li>{t('step2.result3')}</li>
                <li>{t('step2.result4')}</li>
              </ul>
              <p className="mt-[8px]">{t('step2.resultOutro')}</p>
            </div>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/market-analysis">{t('step2.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Шаг 3: Персона */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step3.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step3.intro')}</p>
              <p>{t('step3.description')}</p>
            </div>
            <img
              src={helpImg('step3-persona-form.png')}
              alt="Форма создания персоны"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <img
              src={helpImg('step3-persona-result.png')}
              alt="Результат создания персоны"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              <p className="mb-[8px]">{t('step3.resultIntro')}</p>
              <ul className="list-disc pl-[24px] space-y-[8px]">
                <li>{t('step3.result1')}</li>
                <li>{t('step3.result2')}</li>
                <li>{t('step3.result3')}</li>
                <li>{t('step3.result4')}</li>
                <li>{t('step3.result5')}</li>
                <li>{t('step3.result6')}</li>
                <li>{t('step3.result7')}</li>
                <li>{t('step3.result8')}</li>
              </ul>
            </div>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/personas">{t('step3.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Шаг 4: Исследования */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step4.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step4.intro')}</p>
              <p className="italic">{t('step4.optional')}</p>
            </div>
            {lang !== 'en' && (
              <img
                src={helpImg('step4-research-list.png')}
                alt="Страница исследований"
                className="w-full h-auto rounded-[8px] object-cover"
              />
            )}
            <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step4.description')}
            </p>
            {lang !== 'en' && (
              <img
                src={helpImg('step4-research-chat.png')}
                alt="Чат с персоной"
                className="w-full h-auto rounded-[8px] object-cover"
              />
            )}
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/research">{t('step4.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Шаг 5: Позиционирование и УЦП */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step5.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step5.intro')}</p>
              <p>{t('step5.description1')}</p>
              <p>{t('step5.description2')}</p>
              <p>{t('step5.description3')}</p>
            </div>
            {lang !== 'en' && (
              <img
                src={helpImg('step5-positioning-form.png')}
                alt="Форма позиционирования"
                className="w-full h-auto rounded-[8px] object-cover"
              />
            )}
            <img
              src={helpImg('step5-positioning-result.png')}
              alt="Результат позиционирования"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              <p className="mb-[8px]">{t('step5.resultIntro')}</p>
              <ul className="list-disc pl-[24px] space-y-[8px]">
                <li>{t('step5.result1')}</li>
                <li>{t('step5.result2')}</li>
                <li>{t('step5.result3')}</li>
                <li>{t('step5.result4')}</li>
                <li>{t('step5.result5')}</li>
                <li>{t('step5.result6')}</li>
              </ul>
            </div>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/positioning">{t('step5.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Шаг 6: План запуска MVP */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('step6.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('step6.intro')}</p>
              <p>{t('step6.description')}</p>
            </div>
            {lang !== 'en' && (
              <img
                src={helpImg('step6-mvp-form.png')}
                alt="Форма плана MVP"
                className="w-full h-auto rounded-[8px] object-cover"
              />
            )}
            <img
              src={helpImg('step6-mvp-result.png')}
              alt="Результат плана MVP"
              className="w-full h-auto rounded-[8px] object-cover"
            />
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
              <p className="mb-[8px]">{t('step6.resultIntro')}</p>
              <ul className="list-disc pl-[24px] space-y-[8px]">
                <li>{t('step6.result1')}</li>
                <li>{t('step6.result2')}</li>
                <li>{t('step6.result3')}</li>
                <li>{t('step6.result4')}</li>
                <li>{t('step6.result5')}</li>
                <li>{t('step6.result6')}</li>
                <li>{t('step6.result7')}</li>
                <li>{t('step6.result8')}</li>
                <li>{t('step6.result9')}</li>
                <li>{t('step6.result10')}</li>
                <li>{t('step6.result11')}</li>
                <li>{t('step6.result12')}</li>
              </ul>
            </div>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <LocalizedLink to="/mvp-plan">{t('step6.button')}</LocalizedLink>
            </Button>
          </section>

          {/* Разделитель */}
          <hr className="border-[var(--grey-accent-,#eeeeee)]" />

          {/* Этап 2 */}
          <section className="flex flex-col gap-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">
              {t('phase2.title')}
            </h3>
            <div className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] space-y-[8px]">
              <p>{t('phase2.intro')}</p>
              <p>{t('phase2.description1')}</p>
              <p>{t('phase2.listIntro')}</p>
              <ol className="list-decimal pl-[24px] space-y-[8px]">
                <li>{t('phase2.list1')}</li>
                <li>{t('phase2.list2')}</li>
                <li>{t('phase2.list3')}</li>
                <li>{t('phase2.list4')}</li>
                <li>{t('phase2.list5')}</li>
                <li>{t('phase2.list6')}</li>
              </ol>
              <p>{t('phase2.description2')}</p>
              <p>{t('phase2.description3')}</p>
            </div>
            <Button
              asChild
              className="w-full bg-[var(--accent-background,rgba(33,151,241,0.2))] hover:bg-[#2197f1] hover:text-white text-[color:var(--main-brand,#2197f1)] rounded-[16px] h-[36px] font-segoe font-semibold text-[14px] leading-[20px]"
            >
              <a href={lang === 'en' ? "https://create-products.com/en/service" : "https://create-products.com/service"} target="_blank" rel="noopener noreferrer">
                {t('phase2.button')}
              </a>
            </Button>
          </section>
        </div>
      </div>
    </>
  );
}
